/** What syncs built from the product (tours, aliases, itinerary cards, activities, tags) and
 *  whether future updates may touch each one. Decouple / re-couple open the coupling dialog,
 *  rendered by the drawer's host as a SIBLING of the drawer (a dialog inside a drawer is
 *  aria-hidden — lessons.md). */
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { CatalogueOutput } from '@/api/catalogue';
import { formatDate } from '@/utils/format';

import { entityLabel } from './catalogueModel';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export function OutputsPanel({
  outputs,
  onCoupling,
}: {
  outputs: readonly CatalogueOutput[];
  onCoupling: (output: CatalogueOutput) => void;
}) {
  if (!outputs.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing built yet: the first sync that includes this product creates its outputs.
      </Typography>
    );
  }
  return (
    <Table size="small" aria-label="Outputs">
      <TableHead>
        <TableRow>
          <TableCell>Output</TableCell>
          <TableCell>Built</TableCell>
          <TableCell>Updates</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {outputs.map((o) => (
          <TableRow key={o.id}>
            <TableCell>
              <Typography variant="body2">{entityLabel(o.entity)}</Typography>
              <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.secondary', wordBreak: 'break-all' }}>
                {o.ext_id}
                {o.option_id ? ` · option ${o.option_id}` : ''}
              </Typography>
            </TableCell>
            <TableCell>
              {o.ai_built ? (
                <Chip size="small" label="AI-built" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  deterministic
                </Typography>
              )}
              {o.updated_ts && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatDate(o.updated_ts)}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              {o.coupled ? (
                <Chip size="small" icon={<LinkIcon />} label="coupled" variant="outlined" sx={{ height: 22 }} />
              ) : (
                <>
                  <Chip size="small" icon={<LinkOffIcon />} label="decoupled" color="warning" sx={{ height: 22 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    {o.coupling_changed_by ? `${o.coupling_changed_by.name || o.coupling_changed_by.email}` : ''}
                    {o.coupling_changed_ts ? ` · ${formatDate(o.coupling_changed_ts)}` : ''}
                    {o.coupling_note ? ` · “${o.coupling_note}”` : ''}
                  </Typography>
                </>
              )}
            </TableCell>
            <TableCell align="right">
              {o.entity === 'alias' ? (
                <Typography variant="caption" color="text.secondary">
                  follows its tour
                </Typography>
              ) : (
                <Button size="small" onClick={() => onCoupling(o)}>
                  {o.coupled ? 'Decouple…' : 'Re-couple…'}
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
